import axios from 'axios';
import Swal from 'sweetalert2';
import collect from 'collect.js';
import FormHelperMixin from '@SHARED/js/Vue/mixins/FormHelperMixin';
import { formatPrice } from '@SHARED/js/helpers/misc';
import template from './PaymentMethodComponent.html';
import store from '../../store/store';
import klarna from '../../services/KlarnaPayments/klarna';

export default {
  template,

  mixins: [FormHelperMixin],

  props: {
    allowedToContinue: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      selectedMethod: null,
      klarnaOptions: {
        selectedMethod: 'pay_now',
      },
      sharedState: store, // Do not modify this directly – this is only for convenience in templates.
      isValid: false,
      isLoading: true,
    };
  },

  watch: {
    selectedMethod(val) {
      this.clearErrors();

      if (val === 'KLARNA') {
        store.info.paymentMethod = 'KLARNA';
        // Only valid once Klarna Widget is loaded
        this.isValid = false;
        this.loadKlarnaWidget();
      }

      if (val === 'INVOICE') {
        store.info.paymentMethod = 'INVOICE';
        this.isValid = true;
      }

      store.setPreferredPaymentMethod(val);
      this.submitDataLayerCheckoutPaymentMethod();
    },

    'klarnaOptions.selectedMethod': {
      handler() {
        this.clearErrors();
        this.isValid = false;

        // Klarna widget needs to be reloaded in order to attach the widget to the correct element
        this.loadKlarnaWidget();
      },
    },

    isValid(val) {
      if (val === true) {
        this.isLoading = false;
      }
    },
  },

  computed: {
    sum() {
      if (this.selectedMethod) {
        return store.getOrderStatement().amountWithoutVAT;
      }

      return null;
    },

    totalAmount() {
      if (this.selectedMethod) {
        return store.getOrderStatement().totalAmount;
      }

      return null;
    },

    taxAmount() {
      if (this.selectedMethod) {
        return store.getOrderStatement().taxAmount;
      }

      return null;
    },

    shippingAmount() {
      if (this.selectedMethod) {
        return store.getOrderStatement().shippingAmount;
      }

      return null;
    },
  },

  mounted() {
    klarna.initSession({
      successCallback: () => {
        if (typeof window.Klarna === 'undefined') {
          // Klarna library not yet loaded; add ready-callback
          window.klarnaAsyncCallback = () => this.initKlarnaWidget();
        } else {
          this.initKlarnaWidget();
        }
      },
      failureCallback: (e) => {
        Swal.fire(
          'Noe gikk galt',
          'Vennligst prøv igjen. Om problemet vedvarer, ring Kraftex (<a href="tel:69252530">69 25 25 30</a>).<br>' +
          'Feil utenom våre åpningstider melder du <a href="/kontakt-oss" target="_blank">her</a>, så tar vi kontakt med deg.',
          'error',
        );

        window.bugsnagClient && window.bugsnagClient.notify(e);
        throw new Error(e);
      },
      finalCallback: () => {
        this.isLoading = false;
      },
    });

    if (this.selectedMethod !== null) {
      this.submitDataLayerCheckoutPaymentMethod();
    }
  },

  methods: {
    // Klarna Widget requires library loaded from CDN
    initKlarnaWidget() {
      try {
        window.Klarna.Payments.init({
          client_token: klarna.currentSession().clientToken,
        });
      } catch (e) {
        Swal.fire(
          'Noe gikk galt',
          'Vennligst prøv igjen. Om problemet vedvarer, ring Kraftex (<a href="tel:69252530">69 25 25 30</a>).<br>' +
          'Feil utenom våre åpningstider melder du <a href="/kontakt-oss" target="_blank">her</a>, så tar vi kontakt med deg.',
          'error',
        );

        window.bugsnagClient && window.bugsnagClient.notify(e);
        throw new Error(e);
      }
    },

    loadKlarnaWidget() {
      // NOTE: We cannot load widget if initKlarnaWidget() has not returned results for session.paymentMethodCategories
      if (!store.cart.session.paymentMethodCategories.length) {
        this.addAttrError(
          'klarnaError',
          'Kunne ikke hente betalingsalternativer fra Klarna, kontroller at opplysningene er riktige.',
        );

        throw new Error('Failed to load Klarna widget: paymentMethodCategories is empty');
      }

      // Since the widget doesn't have a destroy method, we need to remove any leftover traces by it if the widget
      // had already been loaded (e.g this.reload() was used)
      $(this.$el).find('.klarna-payments-container').empty();

      // Ensure DOM is rendered
      this.$nextTick(() => {
        try {
          window.Klarna.Payments.load({
            container: `#klarna-payments-container_${this.klarnaOptions.selectedMethod}`,
            payment_method_category: this.klarnaOptions.selectedMethod,
          }, (response) => {
            if (response.show_form === true && typeof response.error === 'undefined') {
              this.isValid = true;
            }

            // The response.error object is only available in case of user-solvable Klarna errors
            if (typeof response.error !== 'undefined') {
              window.bugsnagClient && window.bugsnagClient.notify(response.error);
              this.errors = response.error;
            }

            console.log('Klarna load() callback; ', response);
          });
        } catch (e) {
          Swal.fire(
            'Noe gikk galt',
            'Vennligst prøv igjen. Om problemet vedvarer, ring Kraftex (<a href="tel:69252530">69 25 25 30</a>).<br>' +
            'Feil utenom våre åpningstider melder du <a href="/kontakt-oss" target="_blank">her</a>, så tar vi kontakt med deg.',
            'error',
          );

          window.bugsnagClient && window.bugsnagClient.notify(e);
          throw new Error(e);
        }
      });
    },

    confirmPaymentMethod() {
      if (this.isValid && !this.isLoading && this.allowedToContinue) {
        this.clearErrors();
        this.$emit('confirming-payment-method');

        if (this.selectedMethod === 'KLARNA') {
          try {
            // Authorization guarantees that the order can be created within 60 minutes using finalize()...
            window.Klarna.Payments.authorize(
              {
                payment_method_category: this.klarnaOptions.selectedMethod,
                auto_finalize: false, // Finalize in the next step
              },
              {},
              (response) => {
                console.log('Klarna authorize() callback; ', response);

                if (response.approved === true && response.finalize_required) {
                  // Some payment methods require an additional step by calling finalize(),
                  window.Klarna.Payments.finalize(
                    { payment_method_category: this.klarnaOptions.selectedMethod },
                    {},
                    (finalizeResponse) => {
                      console.log('Klarna finalize() callback; ', finalizeResponse);

                      if (finalizeResponse.approved && finalizeResponse.authorization_token) {
                        store.cart.session = Object.assign(
                          {},
                          store.cart.session,
                          { authorizationToken: finalizeResponse.authorization_token },
                        );

                        this.$emit('confirmed-payment-method');
                      }

                      if (!finalizeResponse.approved) {
                        // The response.error object is only available in case of user-solvable Klarna errors
                        if (typeof finalizeResponse.error !== 'undefined') {
                          window.bugsnagClient && window.bugsnagClient.notify(finalizeResponse.error);
                          this.errors = finalizeResponse.error;
                        }

                        this.$emit('denied-payment-method');
                      }
                    });
                } else if (response.approved === true && !response.finalize_required && response.authorization_token) {
                  store.cart.session = Object.assign(
                    {},
                    store.cart.session,
                    { authorizationToken: response.authorization_token },
                  );

                  this.$emit('confirmed-payment-method');
                }

                if (!response.approved) {
                  // The response.error object is only available in case of user-solvable Klarna errors
                  if (typeof response.error !== 'undefined') {
                    window.bugsnagClient && window.bugsnagClient.notify(response.error);
                    this.errors = response.error;
                  }

                  this.$emit('denied-payment-method');
                }
              },
            );
          } catch (e) {
            Swal.fire(
              'Noe gikk galt',
              'Vennligst prøv igjen. Om problemet vedvarer, ring Kraftex (<a href="tel:69252530">69 25 25 30</a>).<br>' +
              'Feil utenom våre åpningstider melder du <a href="/kontakt-oss" target="_blank">her</a>, så tar vi kontakt med deg.',
              'error',
            );

            this.$emit('denied-payment-method');

            window.bugsnagClient && window.bugsnagClient.notify(e);
            throw new Error(e);
          }
        }

        if (this.selectedMethod === 'INVOICE') {
          axios.post('/api/v1/payment/validate-invoice', store.customerData)
            .then((response) => {
              this.$emit('confirmed-payment-method', response.data);
            })
            .catch((error) => {
              this.$emit('denied-payment-method');

              if (typeof error.response.data.errors !== 'undefined') {
                this.errors = error.response.data.errors;
                Swal.fire({
                  title: 'Ikke godkjent',
                  html: `
                    <p class="text-left">
                      Fakturainformasjon ikke godkjent. Sjekk at feltene er riktige.<br>
                      <ul class="text-left">
                        ${collect(this.errors).map(e => `<li>${e}</li>`).join('')}
                      </ul>
                    </p>`,
                  type: 'warning',
                });
              } else if (typeof error.response.data.message !== 'undefined') {
                Swal.fire('Noe gikk galt', error.response.data.message, 'error');
                window.bugsnagClient && window.bugsnagClient.notify(error);
              } else {
                Swal.fire(
                  'Noe gikk galt',
                  'Vennligst prøv igjen. Om problemet vedvarer, ring Kraftex (<a href="tel:69252530">69 25 25 30</a>).<br>' +
                  'Feil utenom våre åpningstider melder du <a href="/kontakt-oss" target="_blank">her</a>, så tar vi kontakt med deg.',
                  'error',
                );

                window.bugsnagClient && window.bugsnagClient.notify(error);
                throw new Error(error);
              }
            });
        }
      }
    },

    submitDataLayerCheckoutPaymentMethod() {
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: 'selectPaymentMethod',
        ecommerce: {
          checkout_option: {
            actionField: { step: 4, option: this.selectedMethod },
          },
        },
      });
    },

    formatPrice,

    // Component API
    reload() {
      this.isValid = false;
      this.isLoading = true;

      klarna.updateSession({
        successCallback: () => {
          if (this.selectedMethod === 'KLARNA') {
            this.loadKlarnaWidget();
          }
        },
        finalCallback: () => {
          this.isLoading = false;

          if (this.selectedMethod === 'INVOICE') {
            this.isValid = true;
          }
        },
      });
    },

  },
};
